import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IInsightCurrencyBalance,
  IInsightsHistoryPoint,
  INSIGHTS_UNASSIGNED,
  toDecimal,
} from "shared";
import {
  Else,
  If,
  Then,
  When,
} from "react-if";
import { Tooltip } from "@mui/material";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { Cog6ToothIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useParams } from "react-router";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import { LineChart } from "@mui/x-charts/LineChart";
import { lineClasses } from "@mui/x-charts/LineChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { chartsGridClasses } from "@mui/x-charts/ChartsGrid";

import { useOrgInsights } from "../../../hooks/useOrgInsights";
import { Select } from "../../../../common/Atoms/Select";
import { MultiSelect } from "../../../../common/Atoms/MultiSelect";
import { LoadingElementPlaceholder } from "../../../../common/Atoms/LoadingElementPlaceholder";
import { classNames } from "../../../../common/lib/classNames";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { useFetchContactTagsQuery } from "../../../services/api/contactTagsApi/contactTags";
import { useGroups } from "../../../hooks/useGroups";
import { useCurrentOrgUsers } from "../../../hooks/useCurrentOrgUsers";

interface InsightsProps {
  groupId?: number;
  /** Header title. Defaults to "{org legal name}'s Insights" */
  title?: string;
  /** Hide the tag/exclusion filters (group page - already scoped to the group) */
  hideFilters?: boolean;
}

/**
 * The exclude dropdown mixes groups and tags in one list, so each option value
 * is namespaced and split back out before hitting the API.
 */
const GROUP_EXCLUSION_PREFIX = `group:`;
const TAG_EXCLUSION_PREFIX = `tag:`;

const defaultBalanceState: IInsightCurrencyBalance = {
  currencyCode: ``,
  amountOverdueCents: 0,
  amountOverdueFormatted: ``,
  amountOutstandingCents: 0,
  amountOutstandingFormatted: ``,
  amountCreditCents: 0,
  amountCreditFormatted: ``,
  averageDaysOverdue: 0,
  salesLast30DaysCents: 0,
  salesLast30DaysFormatted: ``,
  daysSalesOutstanding: 0,
  daysSalesOverdue: 0,
  openInvoiceCount: 0,
  overdueInvoiceCount: 0,
  averageInvoiceValueCents: 0,
  averageInvoiceValueFormatted: ``,
  collectionEffectivenessIndex: null,
};

/** How a rise in the metric reads - drives the colour of trend chips and lines */
type TrendDirection = `upGood` | `upBad` | `neutral`;

const PERIOD_OPTIONS = [3, 6, 12];
const PERIOD_STORAGE_KEY = `insightsChartPeriodMonths`;
const CHART_OPEN_STORAGE_KEY = `insightsChartOpen`;

// Line colours for trend-coloured charts (paidniceGreen-500 / paidniceRed-600)
const GOOD_STROKE = `#467691`;
const BAD_STROKE = `#e51d1e`;

interface SparkGeometry {
  line: string;
  area: string;
  last: { x: number; y: number };
}

/**
 * Line + area path for a small inline sparkline. Null months (e.g. a currency
 * that didn't exist yet) are dropped and the real points spread across the full
 * width - a young org's sparkline fills the box instead of huddling right.
 * Needs at least two real points.
 */
function sparkPath(values: (number | null)[], width: number, height: number, pad = 2): SparkGeometry | null {
  const points = values.filter((value): value is number => value !== null);

  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = (max - min) || 1;
  const step = (width - 2 * pad) / (points.length - 1);

  const coords = points.map((value, index) => ({
    x: Number((pad + index * step).toFixed(1)),
    y: Number((height - pad - ((value - min) / range) * (height - 2 * pad)).toFixed(1)),
  }));

  const line = `M${coords.map(coord => `${coord.x},${coord.y}`).join(` L`)}`;
  const last = coords[coords.length - 1];

  return {
    line,
    area: `${line} L${last.x},${height} L${coords[0].x},${height} Z`,
    last,
  };
}

interface TrendSummary {
  /** Change over the window as a % of the first value, rounded */
  pct: number;
  /** null = neutral metric (no good/bad reading) */
  good: boolean | null;
}

/** First non-null vs last non-null value of the window. Null when there's nothing to compare */
function trendOf(values: (number | null)[], direction: TrendDirection): TrendSummary | null {
  const numbers = values.filter((value): value is number => value !== null);

  if (numbers.length < 2) return null;

  const first = numbers[0];
  const last = numbers[numbers.length - 1];

  if (first === 0) return null;

  const pct = Math.round(((last - first) / Math.abs(first)) * 100);

  return {
    pct,
    good: direction === `neutral` || pct === 0 ? null : (pct > 0) === (direction === `upGood`),
  };
}

interface TrendChipProps {
  trend: TrendSummary | null;
  spanLabel: string;
  /** Sits on the dark header, so use the light end of each colour ramp */
  onDark?: boolean;
}

/** "↑ 12% over 12 mo" chip, coloured by whether the move is good or bad */
function TrendChip({ trend, spanLabel, onDark = false }: TrendChipProps) {
  if (!trend) return null;

  const colour = onDark
    ? trend.good === null
      ? `text-paidniceGreen-300`
      : trend.good ? `text-emerald-200 dark:text-[#a7f3d0]` : `text-paidniceRed-200`
    : trend.good === null
      ? `text-gray-500`
      : trend.good ? `text-emerald-700` : `text-paidniceRed-700`;

  return (
    <span className={ classNames(`inline-flex items-center gap-0.5 text-xs font-semibold`, colour) }>
      <When condition={ trend.pct !== 0 }>
        { trend.pct > 0 ? <ArrowUpIcon className={ `h-3 w-3` } /> : <ArrowDownIcon className={ `h-3 w-3` } /> }
      </When>
      { `${Math.abs(trend.pct)}% over ${spanLabel}` }
    </span>
  );
}

interface MetricLabelProps {
  tooltip: string;
  children: ReactNode;
  className?: string;
}

/** Longer explanation of CEI than a tooltip can hold, opened from the tile's help icon */
function CeiHelpPopover() {
  return (
    <Popover className={ `inline-flex` }>
      <PopoverButton
        type={ `button` }
        aria-label={ `What is collections effectiveness (CEI)?` }
        className={ `cursor-pointer rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-paidniceGreen-500` }
      >
        <QuestionMarkCircleIcon className={ `h-4 w-4` } />
      </PopoverButton>
      <PopoverPanel
        anchor={ { to: `bottom end`, gap: 6 } }
        className={ `z-20 w-80 space-y-2 rounded-lg border border-gray-200 bg-white p-3.5 text-xs text-gray-600 shadow-xl` }
      >
        <div className={ `text-[11px] font-semibold uppercase tracking-wide text-gray-400` }>{ `Collections effectiveness index` }</div>
        <p>
          { `Of everything that was due for payment over the last 90 days, how much did you actually collect.` }
        </p>
        <p className={ `rounded bg-gray-50 px-2 py-1.5 font-mono text-[11px] text-gray-700` }>
          { `(opening AR + sales - closing AR) / (opening AR + sales - closing AR not yet due)` }
        </p>
        <p>
          { `Invoices that are not due yet are left out, so raising invoices in batches or late in the month does not drag the number down the way overdue % or DSO can.` }
        </p>
        <p>
          { `100% means everything that fell due was collected. Around 80% is generally considered good and 90% strong, but your own trend matters more than the benchmark.` }
        </p>
      </PopoverPanel>
    </Popover>
  );
}

/** Small metric label with the explanatory tooltip */
function MetricLabel({ tooltip, children, className }: MetricLabelProps) {
  return (
    <Tooltip title={ tooltip }
      arrow
      placement={ `top` }>
      <div className={ classNames(`text-[11px] font-semibold text-gray-500`, className) }>
        { children }
      </div>
    </Tooltip>
  );
}

export function Insights({ groupId, title, hideFilters = false }: InsightsProps) {
  const [currentCurrency, setCurrentCurrency] = useState(``);
  const [currentBalances, setCurrentBalances] = useState(defaultBalanceState);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState(`dsOverdue`);
  // Chart (and its expensive history request) only load once expanded.
  // Persisted so a refetch/remount doesn't collapse it again
  const [chartOpen, setChartOpen] = useState(() => window.localStorage.getItem(CHART_OPEN_STORAGE_KEY) === `true`);

  const toggleChart = useCallback((open: boolean) => {
    setChartOpen(open);
    window.localStorage.setItem(CHART_OPEN_STORAGE_KEY, String(open));
  }, []);

  const showMetric = useCallback((key: string) => {
    setSelectedMetric(key);
    toggleChart(true);
  }, [toggleChart]);
  // Persisted so a refetch/remount doesn't snap the toggle back to 12m
  const [periodMonths, setPeriodMonths] = useState(() => {
    const saved = Number(window.localStorage.getItem(PERIOD_STORAGE_KEY));

    return PERIOD_OPTIONS.includes(saved) ? saved : 12;
  });

  const pickPeriod = useCallback((months: number) => {
    setPeriodMonths(months);
    window.localStorage.setItem(PERIOD_STORAGE_KEY, String(months));
  }, []);
  const currentOrg = useGetSelectedOrganisation();
  const { organisationId } = useParams<{ organisationId: string }>();

  const timezone = currentOrg?.validatedTimezone || `UTC`;

  const [asOfDate, setAsOfDate] = useState(DateTime.now().setZone(timezone).toISODate() as string);

  const excludedTags = useMemo(() => {
    return exclusions
      .filter(value => value.startsWith(TAG_EXCLUSION_PREFIX))
      .map(value => value.slice(TAG_EXCLUSION_PREFIX.length));
  }, [exclusions]);

  const excludedGroupIds = useMemo(() => {
    // Group exclusions are meaningless once scoped to a single group, and
    // excluding the scoped group would empty the widget entirely
    if (groupId) {
      return [];
    }

    return exclusions
      .filter(value => value.startsWith(GROUP_EXCLUSION_PREFIX))
      .map(value => Number(value.slice(GROUP_EXCLUSION_PREFIX.length)));
  }, [exclusions, groupId]);

  const { data: insights, isLoading, isFetching } = useOrgInsights({
    groupId,
    asOfDate,
    tags: selectedTags,
    excludeTags: excludedTags,
    excludeGroupIds: excludedGroupIds,
    assignedUserId: hideFilters ? undefined : assignedUserId || undefined,
  });

  // The 12 month series is ~12x the query cost of the point-in-time figures, so
  // it loads separately - the current numbers paint first, charts fill in after
  const { data: historyResponse, isFetching: isHistoryFetching } = useOrgInsights({
    groupId,
    asOfDate,
    tags: selectedTags,
    excludeTags: excludedTags,
    excludeGroupIds: excludedGroupIds,
    assignedUserId: hideFilters ? undefined : assignedUserId || undefined,
    history: true,
    skip: !chartOpen,
  });

  const historyPoints = historyResponse?.history;

  const { data: tagsResponse } = useFetchContactTagsQuery(
    { organisationId: organisationId as string },
    { skip: !organisationId },
  );

  const { data: groups } = useGroups();

  const orgUsers = useCurrentOrgUsers();

  const assigneeOptions = useMemo(() => {
    return [{ label: `Unassigned`, value: INSIGHTS_UNASSIGNED }, ...orgUsers];
  }, [orgUsers]);

  // When org changes, reset
  useEffect(() => {
    if (currentOrg) {
      const tz = currentOrg.validatedTimezone || `UTC`;

      setAsOfDate(DateTime.now().setZone(tz).toISODate() as string);
      setSelectedTags([]);
      setExclusions([]);
      setAssignedUserId(null);
    }
  }, [currentOrg]);

  const possibleCurrencies = useMemo(() => {
    if (!insights) return [];

    return Object.keys(insights.balances);
  }, [insights]);

  useEffect(() => {
    if (currentOrg) {
      const homeCurrency = currentOrg.baseCurrency;
      const currencyToDefault = possibleCurrencies.includes(homeCurrency) ? homeCurrency : possibleCurrencies[0];
      if (!currentCurrency && possibleCurrencies.length > 0) {
        setCurrentCurrency(currencyToDefault);
      }

      if (!possibleCurrencies.includes(currentCurrency)) {
        setCurrentCurrency(currencyToDefault);
      }
    }
  }, [possibleCurrencies, currentCurrency, currentOrg]);

  useEffect(() => {
    if (!insights) return;

    if (insights.balances[currentCurrency]) {
      setCurrentBalances(insights.balances[currentCurrency]);
    }
    else {
      // When no balance object at all, or balance object does not have the current currency, set to default
      setCurrentBalances(defaultBalanceState);
    }
  }, [currentCurrency, insights]);

  const formatter = useCallback(() => {
    return new Intl.NumberFormat(`en-US`, {
      style: `currency`,
      currency: currentCurrency || `USD`,
    });
  }, [ currentCurrency ]);

  const tagOptions = useMemo(() => {
    return (tagsResponse?.tags || []).map(tag => ({ label: tag.name, value: tag.name }));
  }, [tagsResponse]);

  const exclusionOptions = useMemo(() => {
    // Already scoped to a single group, so excluding groups can only ever empty the widget
    const groupOptions = groupId ? [] : (groups || [])
      .filter(group => !group.deleted && group.id)
      .map(group => ({
        label: group.title,
        value: `${GROUP_EXCLUSION_PREFIX}${group.id}`,
        badge: `Group`,
        badgeColor: `blue`,
      }));

    return [
      ...groupOptions,
      ...(tagsResponse?.tags || []).map(tag => ({
        label: tag.name,
        value: `${TAG_EXCLUSION_PREFIX}${tag.name}`,
        badge: `Tag`,
        badgeColor: `gray`,
      })),
    ];
  }, [groups, groupId, tagsResponse]);

  const asOfDateReadable = useMemo(() => {
    const date = DateTime.fromISO(asOfDate);

    if (date.hasSame(DateTime.now().setZone(timezone), `day`)) {
      return `today`;
    }

    return date.toISODate() as string;
  }, [ asOfDate, timezone ]);

  const formatCents = useCallback((cents: number) => formatter().format(toDecimal(cents)), [formatter]);
  const formatDays = useCallback((days: number) => `${days} day${days === 1 ? `` : `s`}`, []);
  const formatPercentagePoints = useCallback((points: number) => `${points}%`, []);

  // Money axis ticks need the short form ("A$27.5k"), not the full amount
  const formatCentsCompact = useCallback((cents: number) => {
    return new Intl.NumberFormat(`en-US`, {
      style: `currency`,
      currency: currentCurrency || `USD`,
      notation: `compact`,
      maximumFractionDigits: 1,
    }).format(toDecimal(cents));
  }, [currentCurrency]);

  // Org wide behavioural stats - independent of the currency selector
  const paymentBehaviour = insights?.paymentBehaviour;

  const avgDaysToPayDisplay = useMemo(() => {
    const avg = paymentBehaviour?.averageDaysToPay;

    if (avg === null || avg === undefined) return null;

    if (avg === 0) return `On time`;

    return avg > 0 ? `${avg} days late` : `${Math.abs(avg)} days early`;
  }, [paymentBehaviour]);

  const outstandingTooltip = `Total outstanding accounts receivable balance${currentBalances.openInvoiceCount ? ` across ${currentBalances.openInvoiceCount} invoices` : ``}`;
  const overdueTooltip = `Total accounts receivable balance overdue${currentBalances.overdueInvoiceCount ? ` across ${currentBalances.overdueInvoiceCount} invoices` : ``}`;
  const dsoTooltip = `Days Sales Outstanding - Outstanding balance divided by average daily sales over the last 30 days`;
  const dsOverdueTooltip = `Days Sales Overdue - Overdue balance divided by average daily sales over the last 30 days`;
  const ceiTooltip = `Collections Effectiveness Index - Share of what was due in the 90 days up to ${asOfDateReadable} that was collected. Invoices not yet due do not count against it`;
  const sales30Tooltip = `Total sales in the 30 days up to ${asOfDateReadable}`;
  const avgOpenValueTooltip = `Average total value of the ${currentBalances.openInvoiceCount} open invoice${currentBalances.openInvoiceCount === 1 ? `` : `s`} as of ${asOfDateReadable}`;
  const avgDaysToPayTooltip = paymentBehaviour
    ? `Average days from due date to full payment for the ${paymentBehaviour.paidInvoiceCount} invoices fully paid in the ${paymentBehaviour.windowDays} days up to ${asOfDateReadable}, across all currencies`
    : ``;
  const onTimeTooltip = paymentBehaviour
    ? `Share of the ${paymentBehaviour.paidInvoiceCount} invoices fully paid in the ${paymentBehaviour.windowDays} days up to ${asOfDateReadable} that were paid on or before their due date, across all currencies`
    : ``;

  const hasOnTimeRate = paymentBehaviour?.onTimePaymentRate !== null && paymentBehaviour?.onTimePaymentRate !== undefined;
  const hasPromiseRate = paymentBehaviour?.promiseKeptRate !== null && paymentBehaviour?.promiseKeptRate !== undefined;
  const promiseTooltip = paymentBehaviour
    ? `Share of the ${(paymentBehaviour.promisesKeptCount || 0) + (paymentBehaviour.promisesBrokenCount || 0)} payment promises resolved in the ${paymentBehaviour.windowDays} days up to ${asOfDateReadable} that were paid in time`
    : ``;

  const activeFilterCount = selectedTags.length + exclusions.length + (assignedUserId ? 1 : 0);

  const selectors = (
    <div className={ `flex flex-wrap items-center gap-2` }>
      <When condition={ possibleCurrencies.length > 1 }>
        <div className={ `w-40` }>
          <Select
            options={ possibleCurrencies.map(currency => ({ label: currency, value: currency })) }
            onChange={ value => setCurrentCurrency(value.value) }
            selected={ currentCurrency }
          />
        </div>
      </When>

      <When condition={ !hideFilters }>
        <Popover className={ `relative` }>
          <PopoverButton
            className={ `relative rounded-md p-1.5 text-paidniceGreen-300 hover:bg-white/10 hover:text-white focus:outline-none` }
            aria-label={ `Insight filters` }
          >
            <Cog6ToothIcon className={ `h-5 w-5` } />
            <When condition={ activeFilterCount > 0 }>
              <span className={ `absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-secondary px-1 text-[10px] font-bold text-white` }>
                { activeFilterCount }
              </span>
            </When>
          </PopoverButton>

          { /* Anchored so it portals out of the widget's overflow-hidden shell. Headless UI
               inline-styles a viewport-fitted max-height + overflow:auto on anchored panels,
               which this panel is far too short to need - hence the two overrides */ }
          <PopoverPanel
            anchor={ { to: `bottom end`, gap: 8 } }
            className={ `z-20 max-h-none! w-56 space-y-3 overflow-visible! rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5` }
          >
            <div>
              <div className={ `mb-1 text-[11px] font-semibold text-gray-500` }>
                { `Assignee` }
              </div>
              <Select
                options={ assigneeOptions }
                selected={ assignedUserId }
                onChange={ ({ value }) => setAssignedUserId(value) }
                emptyText={ `All assignees` }
                nullable
                size={ `sm` }
              />
            </div>

            <When condition={ tagOptions.length > 0 }>
              <div>
                <div className={ `mb-1 text-[11px] font-semibold text-gray-500` }>
                  { `Tags` }
                </div>
                <MultiSelect
                  options={ tagOptions }
                  selected={ selectedTags }
                  onChange={ value => setSelectedTags(value as string[]) }
                  display={ selectedTags.length ? `${selectedTags.length} tag${selectedTags.length > 1 ? `s` : ``}` : `All tags` }
                  clearable
                  size={ `sm` }
                />
              </div>
            </When>

            <When condition={ exclusionOptions.length > 0 }>
              <div>
                <div className={ `mb-1 text-[11px] font-semibold text-gray-500` }>
                  { `Exclusions` }
                </div>
                <MultiSelect
                  options={ exclusionOptions }
                  selected={ exclusions }
                  onChange={ value => setExclusions(value as string[]) }
                  display={ exclusions.length ? `${exclusions.length} excluded` : `No exclusions` }
                  ariaLabel={ `Exclude groups or tags` }
                  clearable
                  size={ `sm` }
                />
              </div>
            </When>
          </PopoverPanel>
        </Popover>
      </When>
    </div>
  );

  /** Month labels for the x axis / tooltips, one per history point */
  const monthLabels = useMemo(() => {
    return (historyPoints || []).map(point => DateTime.fromISO(point.asOfDate).toFormat(`MMM`));
  }, [historyPoints]);

  /**
   * Balance accessors coalesce a missing currency to 0 - a month with no
   * invoices in the currency is a real zero, and leaving it null makes
   * connectNulls draw a false ramp across the gap. Payment behaviour metrics
   * keep null (no paid invoices genuinely means "no reading", not zero).
   */
  const extractSeries = useCallback((extract: (point: IInsightsHistoryPoint) => number | null | undefined) => {
    return (historyPoints || []).map(point => extract(point) ?? null);
  }, [historyPoints]);

  /** Everything the big chart needs - metric tiles and hero figures both qualify */
  interface ChartMetric {
    key: string;
    label: string;
    direction: TrendDirection;
    /** 12 month series for the sparkline + big chart. Empty until history loads */
    series: (number | null)[];
    /** Full-value formatter (chart tooltips) */
    format: (value: number) => string;
    /** Short formatter for the chart's y axis ticks */
    formatAxis: (value: number) => string;
  }

  interface MetricTile extends ChartMetric {
    tooltip: string;
    value: ReactNode;
    danger?: boolean;
    /** Help icon in the tile's corner, for metrics that need more than the tooltip */
    help?: ReactNode;
    /** Metrics with nothing to say are dropped rather than shown as zero */
    visible: boolean;
  }

  const formatDaysAxis = useCallback((days: number) => `${Math.round(days)}d`, []);

  const metricTiles: MetricTile[] = [
    {
      key: `sales30`,
      label: `Sales`,
      tooltip: sales30Tooltip,
      value: formatCents(currentBalances.salesLast30DaysCents),
      direction: `upGood`,
      series: extractSeries(point => point.balances[currentCurrency]?.salesLast30DaysCents ?? 0),
      format: formatCents,
      formatAxis: formatCentsCompact,
      visible: currentBalances.salesLast30DaysCents > 0,
    },
    {
      key: `avgOpenValue`,
      label: `Average open invoice`,
      tooltip: avgOpenValueTooltip,
      value: formatCents(currentBalances.averageInvoiceValueCents),
      direction: `neutral`,
      series: extractSeries(point => point.balances[currentCurrency]?.averageInvoiceValueCents ?? 0),
      format: formatCents,
      formatAxis: formatCentsCompact,
      visible: currentBalances.averageInvoiceValueCents > 0,
    },
    {
      key: `dso`,
      label: `Days sales outstanding (DSO)`,
      tooltip: dsoTooltip,
      value: formatDays(currentBalances.daysSalesOutstanding),
      direction: `upBad`,
      series: extractSeries(point => point.balances[currentCurrency]?.daysSalesOutstanding ?? 0),
      format: formatDays,
      formatAxis: formatDaysAxis,
      visible: currentBalances.daysSalesOutstanding > 0,
    },
    {
      key: `dsOverdue`,
      label: `Days sales overdue`,
      tooltip: dsOverdueTooltip,
      value: formatDays(currentBalances.daysSalesOverdue),
      danger: true,
      direction: `upBad`,
      series: extractSeries(point => point.balances[currentCurrency]?.daysSalesOverdue ?? 0),
      format: formatDays,
      formatAxis: formatDaysAxis,
      visible: currentBalances.daysSalesOverdue > 0,
    },
    {
      key: `cei`,
      label: `Collections effectiveness (CEI)`,
      tooltip: ceiTooltip,
      help: <CeiHelpPopover />,
      value: `${currentBalances.collectionEffectivenessIndex}%`,
      direction: `upGood`,
      series: extractSeries(point => point.balances[currentCurrency]?.collectionEffectivenessIndex ?? null),
      format: formatPercentagePoints,
      formatAxis: formatPercentagePoints,
      visible: currentBalances.collectionEffectivenessIndex !== null && currentBalances.collectionEffectivenessIndex !== undefined,
    },
    {
      key: `avgDaysToPay`,
      label: `Average days to pay`,
      tooltip: avgDaysToPayTooltip,
      value: avgDaysToPayDisplay,
      direction: `upBad`,
      series: extractSeries(point => point.paymentBehaviour.averageDaysToPay),
      format: formatDays,
      formatAxis: formatDaysAxis,
      visible: !!paymentBehaviour && avgDaysToPayDisplay !== null,
    },
    {
      key: `onTime`,
      label: `On-time payments`,
      tooltip: onTimeTooltip,
      value: `${paymentBehaviour?.onTimePaymentRate}%`,
      direction: `upGood`,
      series: extractSeries(point => point.paymentBehaviour.onTimePaymentRate),
      format: formatPercentagePoints,
      formatAxis: formatPercentagePoints,
      visible: hasOnTimeRate,
    },
    {
      key: `promisesKept`,
      label: `Promises kept`,
      tooltip: promiseTooltip,
      value: hasPromiseRate ? `${paymentBehaviour?.promiseKeptRate}%` : `No promises`,
      direction: `upGood`,
      series: extractSeries(point => point.paymentBehaviour.promiseKeptRate ?? null),
      format: formatPercentagePoints,
      formatAxis: formatPercentagePoints,
      // Shown even with no resolved promises in the window
      visible: true,
    },
  ];

  const visibleTiles = metricTiles.filter(tile => tile.visible);

  /* The hero figures are chartable too - clicking one opens it in the big chart */
  const heroMetrics: ChartMetric[] = [
    {
      key: `outstanding`,
      label: `Total outstanding`,
      direction: `upBad`,
      series: extractSeries(point => point.balances[currentCurrency]?.amountOutstandingCents ?? 0),
      format: formatCents,
      formatAxis: formatCentsCompact,
    },
    {
      key: `overdue`,
      label: `Total overdue`,
      direction: `upBad`,
      series: extractSeries(point => point.balances[currentCurrency]?.amountOverdueCents ?? 0),
      format: formatCents,
      formatAxis: formatCentsCompact,
    },
  ];

  // The selected metric can disappear (filters/currency change) - fall back to the first visible
  const activeMetric: ChartMetric | undefined = [...heroMetrics, ...visibleTiles].find(metric => metric.key === selectedMetric)
    || visibleTiles[0]
    || heroMetrics[0];

  /* Design 1d - headline figures with 12 month sparklines on the dark header */
  interface HeroProps {
    metric: ChartMetric;
    tooltip: string;
    value: ReactNode;
    valueClass: string;
    stroke: string;
    fill: string;
  }

  const hero = ({ metric, tooltip, value, valueClass, stroke, fill }: HeroProps) => {
    const spark = sparkPath(metric.series, 260, 40, 3);
    const trend = trendOf(metric.series, metric.direction);

    return (
      <button type={ `button` }
        onClick={ () => showMetric(metric.key) }
        aria-label={ `Show ${metric.label} chart` }
        className={ `flex flex-col gap-1 rounded-lg text-left` }>
        <MetricLabel tooltip={ tooltip }
          className={ `text-[13px] font-normal text-paidniceGreen-300` }>
          { metric.label }
        </MetricLabel>
        <div className={ `flex flex-wrap items-end gap-x-4 gap-y-1` }>
          <span className={ classNames(`text-3xl font-bold leading-none tabular-nums sm:text-[34px]`, valueClass) }>
            { value }
          </span>
          <When condition={ !!spark }>
            <svg width={ 260 }
              height={ 40 }
              viewBox={ `0 0 260 40` }
              className={ `hidden overflow-visible sm:block` }
              aria-hidden>
              <path d={ spark?.area }
                fill={ fill } />
              <path d={ spark?.line }
                fill={ `none` }
                stroke={ stroke }
                strokeWidth={ 2 } />
              <circle cx={ spark?.last.x }
                cy={ spark?.last.y }
                r={ 3 }
                fill={ stroke } />
            </svg>
          </When>
        </div>
        <TrendChip trend={ trend }
          spanLabel={ `12 mo` }
          onDark />
      </button>
    );
  };

  const heroFigures = (
    <div className={ `flex flex-col gap-4 sm:flex-row sm:gap-14` }>
      { hero({
        metric: heroMetrics[0],
        tooltip: outstandingTooltip,
        value: currentBalances.amountCreditCents !== 0 ? (
          <Tooltip
            title={ `${formatCents(currentBalances.amountOutstandingCents - currentBalances.amountCreditCents)} (less ${formatCents(currentBalances.amountCreditCents)} credit)` }
          >
            <span>{ formatCents(currentBalances.amountOutstandingCents) }</span>
          </Tooltip>
        ) : (
          formatCents(currentBalances.amountOutstandingCents)
        ),
        valueClass: `text-white`,
        stroke: `#8ab1c7`,
        fill: `rgba(138,177,199,.18)`,
      }) }

      <div className={ `sm:border-l sm:border-paidniceGreen-700 sm:pl-14` }>
        { hero({
          metric: heroMetrics[1],
          tooltip: overdueTooltip,
          value: formatCents(currentBalances.amountOverdueCents),
          valueClass: `text-paidniceRed-200`,
          stroke: `#ffa0a1`,
          fill: `rgba(255,160,161,.16)`,
        }) }
      </div>
    </div>
  );

  /* Design 1d - the tiles act as tabs over the big chart below */
  const metricStrip = (
    <div className={ `grid grid-cols-2 gap-px bg-gray-100 lg:grid-cols-4` }
      role={ `tablist` }
      aria-label={ `Insight metrics` }>
      {
        visibleTiles.map(tile => {
          const selected = tile.key === activeMetric?.key;
          const trend = trendOf(tile.series, tile.direction);
          const stroke = trend?.good === false ? BAD_STROKE : GOOD_STROKE;
          const tinySpark = sparkPath(tile.series, 70, 16, 1.5);

          return (
            <div key={ tile.key }
              className={ `relative` }>
              <button
                type={ `button` }
                role={ `tab` }
                aria-selected={ selected }
                onClick={ () => showMetric(tile.key) }
                /* Accent sits on the bottom edge, pointing at the chart it controls -
                   a top accent reads as the dark header bleeding into the tile */
                className={ classNames(
                  `flex h-full w-full cursor-pointer flex-col gap-1 border-b-[3px] px-4 pb-2.5 pt-3 text-left transition-colors`,
                  tile.help && `pr-8`,
                  selected
                    ? `border-paidniceGreen-600 bg-paidniceGreen-50`
                    : `border-transparent bg-white hover:bg-paidniceGreen-50/60`,
                ) }>
                <MetricLabel tooltip={ tile.tooltip }
                  className={ `min-h-8 text-[11.5px] font-normal` }>
                  { tile.label }
                </MetricLabel>
                <div className={ `flex items-center justify-between gap-2` }>
                  <span className={ classNames(
                    `whitespace-nowrap text-[15px] font-bold tabular-nums`,
                    tile.danger ? `text-paidniceRed-700` : `text-paidniceGreen-900`,
                  ) }>
                    { tile.value }
                  </span>
                  <When condition={ !!tinySpark }>
                    <svg width={ 52 }
                      height={ 16 }
                      viewBox={ `0 0 70 16` }
                      preserveAspectRatio={ `none` }
                      className={ `shrink-0` }
                      aria-hidden>
                      <path d={ tinySpark?.line }
                        fill={ `none` }
                        stroke={ stroke }
                        strokeWidth={ 1.5 } />
                    </svg>
                  </When>
                </div>
              </button>
              { /* Sibling of the tab button, not inside it - a button cannot contain another */ }
              <When condition={ !!tile.help }>
                <div className={ `absolute right-2.5 top-3` }>
                  { tile.help }
                </div>
              </When>
            </div>
          );
        })
      }
    </div>
  );

  /* Big chart for the selected metric with the 3/6/12 month toggle */
  const chartSection = activeMetric && (() => {
    if (!chartOpen) {
      return (
        <div className={ `border-t border-gray-200` }>
          <button type={ `button` }
            onClick={ () => toggleChart(true) }
            aria-expanded={ false }
            className={ `flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 sm:px-7` }>
            <span className={ `text-sm font-bold text-paidniceGreen-900` }>
              { `Monthly trends` }
            </span>
            <ChevronDownIcon className={ `h-5 w-5 text-gray-500` } />
          </button>
        </div>
      );
    }

    const periodSeries = activeMetric.series.slice(-periodMonths);
    const periodLabels = monthLabels.slice(-periodMonths);
    const periodTrend = trendOf(periodSeries, activeMetric.direction);
    const stroke = periodTrend?.good === false ? BAD_STROKE : GOOD_STROKE;

    return (
      <div className={ `border-t border-gray-200 px-4 pb-4 pt-3 sm:px-7` }>
        <div className={ `mb-1 flex flex-wrap items-center justify-between gap-2` }>
          <div className={ `flex flex-wrap items-baseline gap-3` }>
            <span className={ `text-sm font-bold text-paidniceGreen-900` }>
              { activeMetric.label }
            </span>
            <TrendChip trend={ periodTrend }
              spanLabel={ `${periodMonths} mo` } />
          </div>
          <div className={ `flex gap-0.5 rounded-lg bg-gray-100 p-0.5` }>
            {
              PERIOD_OPTIONS.map(months => (
                <button key={ months }
                  type={ `button` }
                  onClick={ () => pickPeriod(months) }
                  className={ classNames(
                    `rounded-md px-3 py-1 text-xs font-semibold transition-colors`,
                    months === periodMonths
                      ? `bg-paidniceGreen-700 text-white`
                      : `text-gray-600 hover:text-paidniceGreen-900`,
                  ) }>
                  { `${months}m` }
                </button>
              ))
            }
            <button type={ `button` }
              onClick={ () => toggleChart(false) }
              aria-expanded
              aria-label={ `Hide chart` }
              className={ `rounded-md p-1 text-gray-500 hover:text-paidniceGreen-900` }>
              <ChevronUpIcon className={ `h-5 w-5` } />
            </button>
          </div>
        </div>

        <If condition={ !historyPoints }>
          <Then>
            <div className={ `py-6` }>
              <LoadingElementPlaceholder rows={ 2 }
                border={ false } />
            </div>
          </Then>
          <Else>
            <div className={ classNames(`transition-opacity duration-500`, isHistoryFetching && `opacity-60`) }>
              <LineChart
                xAxis={ [{ scaleType: `point`, data: periodLabels }] }
                /* Default axis width truncates compact money ticks ("A$27.5k") to "A$2..." */
                yAxis={ [{ width: 64, valueFormatter: (value: number | null) => value === null ? `` : activeMetric.formatAxis(value) }] }
                series={ [{
                  data: periodSeries,
                  label: activeMetric.label,
                  color: stroke,
                  area: true,
                  showMark: true,
                  connectNulls: true,
                  valueFormatter: (value: number | null) => value === null ? `` : activeMetric.format(value),
                }] }
                height={ 210 }
                margin={ { top: 12, right: 12, bottom: 4, left: 4 } }
                grid={ { horizontal: true } }
                hideLegend
                sx={ {
                  [`& .${lineClasses.area}`]: { fillOpacity: 0.12 },
                  [`& .${chartsGridClasses.line}`]: { stroke: `#e5e7eb` },
                  [`& .${axisClasses.line}, & .${axisClasses.tick}`]: { stroke: `#e5e7eb` },
                  [`& .${axisClasses.tickLabel}`]: { fill: `#9ca3af`, fontSize: 10.5 },
                } }
              />
            </div>
          </Else>
        </If>
      </div>
    );
  })();

  const loadingState = (
    <div className={ `p-5` }>
      <LoadingElementPlaceholder rows={ 3 }
        border={ false } />
    </div>
  );

  const headerTitle = title || `${currentOrg?.legalName}'s Insights`;

  return (
    <div className={ `overflow-hidden rounded-xl bg-white ring-1 ring-black/5` }>
      { /* Dark header. Split from the hero band below so the refetch overlay
           can cover the figures without dimming the controls */ }
      <div className={ `flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-[#2F4E60] px-4 pb-4 pt-4 sm:px-7 sm:pb-5 sm:pt-5` }>
        <div className={ `flex flex-wrap items-baseline gap-x-3 gap-y-0.5` }>
          <Tooltip
            title={ `Live values calculated from your invoice and payment data. May differ from your accounting system due to FX rates` }
            arrow
            placement={ `top` }
          >
            <span className={ `text-[15px] font-bold text-white sm:text-[19px]` }>
              { headerTitle }
            </span>
          </Tooltip>
        </div>
        <div className={ `flex flex-wrap items-center gap-3` }>
          { selectors }
        </div>
      </div>

      <If condition={ isLoading }>
        <Then>
          { loadingState }
        </Then>
        <Else>
          { /* Subtle refetch cue: gently dim the figures rather than flashing a spinner overlay */ }
          <div className={ classNames(`transition-opacity duration-500`, isFetching && `opacity-60`) }>
            <div className={ `bg-[#2F4E60] px-4 pb-5 sm:px-7 sm:pb-6` }>
              { heroFigures }
            </div>
            { metricStrip }
            { chartSection }
          </div>
        </Else>
      </If>
    </div>
  );
}
